/**
 * Synthetic user message when the user removes one filter chip.
 * Keeps other filters from that search; never defaults missing location to a city.
 */

const normalizeAmenities = (filters) => (
  Array.isArray(filters.amenities)
    ? filters.amenities.map((a) => String(a).trim()).filter(Boolean)
    : []
);

const marketOptions = (options = {}) => ({
  currency: options.currency || 'LKR',
  countryWideLabel: options.countryWideLabel || 'across Sri Lanka',
});

/** e.g. " with WiFi", " with WiFi and Coffee", " with A, B and C" */
const formatAmenitiesPhrase = (names) => {
  if (!names.length) return '';
  if (names.length === 1) return ` with ${names[0]}`;
  if (names.length === 2) return ` with ${names[0]} and ${names[1]}`;
  return ` with ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const formatRadiusScope = (filters, options = {}) => {
  const { countryWideLabel } = marketOptions(options);
  const radius = filters.radiusKm != null && Number(filters.radiusKm) > 0
    ? Number(filters.radiusKm)
    : null;
  const anchor = (filters.locationAnchor || filters.location || '').trim();
  if (radius && anchor) {
    return `within ${radius} km of ${anchor}`;
  }
  if (anchor) return `in ${anchor}`;
  return countryWideLabel;
};

const buildScopedSearchPhrase = (filters, amenityNames = normalizeAmenities(filters), options = {}) => {
  const { currency } = marketOptions(options);
  const type = filters.spaceType?.trim() || null;
  const scope = formatRadiusScope(filters, options);
  const budget = filters.maxPrice
    ? ` under ${currency} ${Number(filters.maxPrice).toLocaleString()}`
    : '';
  const datePart = filters.date ? ` on ${filters.date}` : '';
  const timePart = filters.timeRange ? ` between ${filters.timeRange}` : '';
  const peoplePart = filters.peopleCount && Number(filters.peopleCount) > 1
    ? ` for ${filters.peopleCount}+ people`
    : '';
  const amenities = formatAmenitiesPhrase(amenityNames);

  if (type) return `show ${type} ${scope}${budget}${datePart}${timePart}${peoplePart}${amenities}`;
  return `show spaces ${scope}${budget}${datePart}${timePart}${peoplePart}${amenities}`;
};

/**
 * @param {'spaceType'|'location'|'radius'|'date'|'time'|'peopleCount'|'maxPrice'|'amenity'|'amenities'} removeKey
 * @param {object} filters - msg.filters from the agent search result
 * @param {{ amenityName?: string, currency?: string, countryWideLabel?: string }} [options]
 * @returns {string}
 */
export const buildChipRemovalMessage = (removeKey, filters = {}, options = {}) => {
  const { currency, countryWideLabel } = marketOptions(options);
  const type = filters.spaceType?.trim() || null;
  const loc = filters.location?.trim() || filters.locationAnchor?.trim() || null;
  const hasRadius = filters.radiusKm != null && Number(filters.radiusKm) > 0;
  const budget = filters.maxPrice
    ? ` under ${currency} ${Number(filters.maxPrice).toLocaleString()}`
    : '';
  const wide = countryWideLabel;

  switch (removeKey) {
    case 'amenity': {
      const name = options.amenityName?.trim();
      if (!name) return '';
      const remaining = normalizeAmenities(filters).filter(
        (a) => a.toLowerCase() !== name.toLowerCase(),
      );
      return buildScopedSearchPhrase(filters, remaining, options);
    }
    case 'amenities':
      return buildScopedSearchPhrase(filters, [], options);
    case 'maxPrice':
      return 'search without a budget limit';
    case 'peopleCount':
      return 'search without a minimum capacity';
    case 'date':
      if (type) return `show ${type} ${formatRadiusScope(filters, options)}${budget} without a specific date`;
      return `show spaces ${formatRadiusScope(filters, options)}${budget} without a specific date`;
    case 'time':
      return buildScopedSearchPhrase({ ...filters, timeRange: null, startTime: null, endTime: null }, normalizeAmenities(filters), options);
    case 'radius': {
      const withoutRadius = {
        ...filters,
        radiusKm: null,
        radiusMiles: null,
      };
      return buildScopedSearchPhrase(withoutRadius, normalizeAmenities(filters), options);
    }
    case 'location':
      if (hasRadius) {
        if (type) return `show ${type} ${wide}${budget}`;
        return `show spaces ${wide}${budget}`;
      }
      if (type) return `show ${type} ${wide}${budget}`;
      return `show spaces ${wide}${budget}`;
    case 'spaceType':
      if (hasRadius && loc) {
        return `show all space types within ${filters.radiusKm} km of ${loc}${budget}`;
      }
      if (loc) return `show all space types in ${loc}${budget}`;
      return `show all space types ${wide}${budget}`;
    default:
      return '';
  }
};

export const formatRadiusFilterLabel = (filters) => {
  const radius = filters?.radiusKm != null && Number(filters.radiusKm) > 0
    ? Number(filters.radiusKm)
    : null;
  const anchor = (filters?.locationAnchor || filters?.location || '').trim();
  if (!radius || !anchor) return null;
  return `Within ${radius} km of ${anchor}`;
};
